import React, { useCallback, useEffect, useMemo, useState } from "react";
import EmptyState from "./EmptyState";
import ProjectCard from "./ProjectCard";
import TrebbleInfiniteList from "../common/InfiniteList";
import TrebbleSkeleton from "../common/TrebbleSkeleton";
import TrebbleFilterButtons from "../common/TrebbleFilterButtons";
import LibrarySearchBar from "./LibrarySearchBar";
import LibraryFilterPopup from "./LibraryFilterPopup";
import LibrarySearchResults from "./LibrarySearchResults";
import LibraryToolbar from "./LibraryToolbar";
import { ProjectCardMenuAction } from "./LibraryItemTypes";
import TrebbleClientAPI from "../../models/helper/TrebbleClientAPI";
import LocalStorageHelper from "../../models/helper/LocalStorageHelper";
import PersistentModels from "../../services/PersistentModels";
import ti18n from "i18n!nls/MyLibraryi18n";
import Utils from "../../models/helper/Utils";
import "./styles.css";

type RecordingMediaType = "audio" | "video";
type ViewMode = "grid" | "list";

type WorkspaceContextValue = {
  workspaceId?: string;
  workspaceName?: string;
  myRole?: string;
  isAdmin?: boolean;
  workspaceStatus?: string;
} | null;

interface RecordingCardData {
  id: string;
  name: string;
  type: RecordingMediaType;
  ownerUserUID?: string | null;
  url?: string;
  audioUrl?: string;
  videoUrl?: string;
  previewUrl?: string;
  duration?: string;
  durationInSeconds?: number;
  transcriptionId?: string | null;
  transcriptionStatus?: string | null;
  date: string;
  createdTimestamp: number;
  creator?: {
    name: string;
    initials: string;
    color: string;
  };
}

type RecordingResponseRecord = Record<string, unknown>;

const PAGE_SIZE = 30;
const RECORDING_SEARCH_PAGE_SIZE = 100;
const WORKSPACE_FILTER_ALL = "all";
const WORKSPACE_FILTER_OWNED = "ownedByMe";

const SORT_OPTIONS: Array<{ key: string; labelKey: string }> = [
  { key: "newest", labelKey: "SORT_NEWEST_FIRST" },
  { key: "oldest", labelKey: "SORT_OLDEST_FIRST" },
  { key: "name_asc", labelKey: "SORT_NAME_ASC" },
  { key: "name_desc", labelKey: "SORT_NAME_DESC" },
];

const t = (key: string): string => window.getI18n(ti18n, key);

const isWorkspaceFeatureEnabled = (): boolean => {
  try {
    const rollout = (window as unknown as Record<string, unknown>).FeatureRolloutHelper as
      { isFeatureEnabled?: (feature: unknown) => boolean; FEATURES?: Record<string, unknown> } | undefined;
    if (rollout && typeof rollout.isFeatureEnabled === "function" && rollout.FEATURES) {
      return rollout.isFeatureEnabled(rollout.FEATURES.WORKSPACE_ENABLED);
    }
    return false;
  } catch {
    return false;
  }
};

const getStoredWorkspaceContext = (): WorkspaceContextValue => {
  try {
    if (!isWorkspaceFeatureEnabled()) {
      return null;
    }
    return LocalStorageHelper.getInstance().getWorkspaceContext() as WorkspaceContextValue;
  } catch {
    return null;
  }
};

const normalizeListResponse = (response: unknown): RecordingResponseRecord[] => {
  if (Array.isArray(response)) {
    return response as RecordingResponseRecord[];
  }
  if (response && typeof response === "object") {
    const responseObject = response as {
      items?: RecordingResponseRecord[];
      recordings?: RecordingResponseRecord[];
      message?: RecordingResponseRecord[];
    };
    if (Array.isArray(responseObject.items)) return responseObject.items;
    if (Array.isArray(responseObject.recordings)) return responseObject.recordings;
    if (Array.isArray(responseObject.message)) return responseObject.message;
  }
  return [];
};

const getColorFromSeed = (seed: string): string => {
  const colors = ["#a8e21d", "#c5a3fb", "#ffa7d4", "#82d9e3", "#70A098", "#4A90D9"];
  let hash = 0;
  for (let index = 0; index < seed.length; index += 1) {
    hash = ((hash << 5) - hash + seed.charCodeAt(index)) | 0;
  }
  return colors[Math.abs(hash) % colors.length];
};

const buildInitials = (name: string): string => (
  name
    .split(" ")
    .filter((token) => token.length > 0)
    .map((token) => token[0])
    .join("")
    .toUpperCase()
    .slice(0, 2) || "?"
);

const formatDate = (value: unknown): { label: string; timestamp: number } => {
  const date = value ? new Date(value as string | number | Date) : null;
  const timestamp = date ? date.getTime() : 0;
  if (!date || Number.isNaN(timestamp)) {
    return { label: "", timestamp: 0 };
  }
  return {
    label: date.toLocaleDateString(undefined, { month: "short", day: "numeric" }),
    timestamp,
  };
};

const formatDuration = (value: unknown): string | undefined => {
  const totalSeconds = parseDurationInSeconds(value);
  if (!totalSeconds || Number.isNaN(totalSeconds) || totalSeconds <= 0) {
    return undefined;
  }
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = Math.floor(totalSeconds % 60);
  return `${minutes}:${seconds.toString().padStart(2, "0")}`;
};

const parseDurationInSeconds = (value: unknown): number | undefined => {
  const totalSeconds =
    typeof value === "number"
      ? value
      : typeof value === "string"
        ? Number(value)
        : 0;
  return totalSeconds && !Number.isNaN(totalSeconds) && totalSeconds > 0 ? totalSeconds : undefined;
};

const getNestedRecord = (value: unknown): RecordingResponseRecord | null => (
  value && typeof value === "object" && !Array.isArray(value)
    ? value as RecordingResponseRecord
    : null
);

const getRecordingTranscriptionId = (recording: RecordingResponseRecord): string | null => {
  const transcriptionInfo = getNestedRecord(recording.transcriptionInfo);
  const candidate = recording.transcriptionId
    || recording.transcriptionUID
    || transcriptionInfo?.transcriptionId
    || transcriptionInfo?._id
    || transcriptionInfo?.id;
  return typeof candidate === "string" && candidate ? candidate : null;
};

const getRecordingTranscriptionStatus = (recording: RecordingResponseRecord): string | null => {
  const transcriptionInfo = getNestedRecord(recording.transcriptionInfo);
  const candidate = recording.trebbleJobStatus
    || recording.transcriptionStatus
    || transcriptionInfo?.trebbleJobStatus
    || transcriptionInfo?.status;
  return typeof candidate === "string" && candidate ? candidate : null;
};

const hasOpenableTranscription = (recording: RecordingCardData): boolean => {
  if (!recording.transcriptionId) return false;
  if (!recording.transcriptionStatus) return true;
  return recording.transcriptionStatus.toLowerCase() === "completed";
};

const getRecordingDisplayName = (
  recording: RecordingResponseRecord,
  mediaType: RecordingMediaType,
  createdInfo: { label: string; timestamp: number },
): string => {
  const originalFilename = typeof recording.originalFilename === "string" ? recording.originalFilename : "";
  if (originalFilename && !/userCreatedCapsule_/i.test(originalFilename)) {
    return originalFilename;
  }

  const rawName = ((recording.name || recording.rawName || "") as string).trim();
  if (rawName && !/^[a-f0-9]{24}\//i.test(rawName) && !/capsulePart_/i.test(rawName)) {
    return rawName;
  }

  return `${mediaType === "video" ? "Video" : "Audio"} recording${createdInfo.label ? ` — ${createdInfo.label}` : ""}`;
};

const buildRecordingCard = (recording: RecordingResponseRecord): RecordingCardData => {
  const recordingId = (recording.assetId || recording._id || recording.id || "") as string;
  const mediaType = ((recording.assetType || "audio") as RecordingMediaType);
  const createdInfo = formatDate(recording.createdDate || recording.creationDate);
  const ownerFullName = ((recording.ownerName || recording.ownerUsername || "Unknown") as string).trim();
  const ownerName = ownerFullName.split(" ")[0] || ownerFullName;
  const ownerSeed = ((recording.ownerUsername || ownerName || recordingId) as string);
  const durationInSeconds = parseDurationInSeconds(recording.durationInSeconds);

  return {
    id: recordingId,
    name: getRecordingDisplayName(recording, mediaType, createdInfo),
    type: mediaType,
    ownerUserUID: (recording.ownerUserUID as string | null | undefined) || null,
    url: (recording.url as string | undefined) || undefined,
    audioUrl: (recording.audioUrl as string | undefined) || (mediaType === "audio" ? (recording.url as string | undefined) : undefined),
    videoUrl: (recording.videoUrl as string | undefined) || (mediaType === "video" ? (recording.url as string | undefined) : undefined),
    previewUrl: (recording.previewUrl as string | undefined) || undefined,
    duration: formatDuration(recording.durationInSeconds),
    durationInSeconds,
    transcriptionId: getRecordingTranscriptionId(recording),
    transcriptionStatus: getRecordingTranscriptionStatus(recording),
    date: createdInfo.label,
    createdTimestamp: createdInfo.timestamp,
    creator: ownerName
      ? {
          name: ownerName,
          initials: buildInitials(ownerFullName),
          color: getColorFromSeed(ownerSeed),
        }
      : undefined,
  };
};

const recordingMatchesSearch = (recording: RecordingCardData, query: string): boolean => {
  const normalizedQuery = query.trim().toLowerCase();
  if (!normalizedQuery) return true;

  return [
    recording.name,
    recording.date,
    recording.duration,
    recording.type,
    recording.creator?.name,
  ]
    .filter(Boolean)
    .join(" ")
    .toLowerCase()
    .includes(normalizedQuery);
};

const RecordingsLibraryPage: React.FC = () => {
  const [workspaceContext, setWorkspaceContext] = useState<WorkspaceContextValue>(() => getStoredWorkspaceContext());
  const currentWorkspaceId = workspaceContext?.workspaceId || null;
  const currentUserId = (() => {
    try {
      return LocalStorageHelper.getInstance().getUserInfo()?._id || null;
    } catch {
      return null;
    }
  })();

  const [viewMode, setViewMode] = useState<ViewMode>("grid");
  const [searchQuery, setSearchQuery] = useState("");
  const [appliedSearch, setAppliedSearch] = useState("");
  const [activeFilter, setActiveFilter] = useState<"all" | RecordingMediaType>("all");
  const [workspaceFilter, setWorkspaceFilter] = useState(WORKSPACE_FILTER_ALL);
  const [activeSort, setActiveSort] = useState("newest");
  const [filterOpen, setFilterOpen] = useState(false);
  const [allCachedRecordings, setAllCachedRecordings] = useState<RecordingCardData[]>([]);
  const [searchResults, setSearchResults] = useState<RecordingCardData[]>([]);
  const [searchLoading, setSearchLoading] = useState(false);
  const [isSearchResultsView, setIsSearchResultsView] = useState(false);
  const [refreshTick, setRefreshTick] = useState(0);

  useEffect(() => {
    const sync = () => setWorkspaceContext(getStoredWorkspaceContext());
    sync();
    window.addEventListener("trebble:workspace-library-updated", sync);
    return () => window.removeEventListener("trebble:workspace-library-updated", sync);
  }, []);

  useEffect(() => {
    setSearchQuery("");
    setAppliedSearch("");
    setSearchResults([]);
    setIsSearchResultsView(false);
    setWorkspaceFilter(WORKSPACE_FILTER_ALL);
  }, [currentWorkspaceId]);

  const fetchRecordingsForContext = useCallback(
    async ({
      pageOffset,
      numberOfResults,
      searchKeyword,
      assetType,
      sortBy,
      ownerUserUID,
    }: {
      pageOffset: number;
      numberOfResults: number;
      searchKeyword?: string;
      assetType?: string | null;
      sortBy?: string;
      ownerUserUID?: string | null;
    }): Promise<{ items: RecordingCardData[]; totalCount: number; hasMore: boolean }> => {
      const response = await TrebbleClientAPI.getInstance().listRecordings({
        workspaceId: currentWorkspaceId || undefined,
        pageOffset,
        numberOfResults,
        searchKeyword: searchKeyword || undefined,
        assetType: assetType || undefined,
        sortBy: sortBy || undefined,
        ownerUserUID: ownerUserUID || undefined,
      });
      const items = normalizeListResponse(response).map(buildRecordingCard);
      const responseObject = response && typeof response === "object" ? (response as { totalCount?: number; hasMore?: boolean }) : {};
      return {
        items,
        totalCount: responseObject.totalCount || items.length,
        hasMore: typeof responseObject.hasMore === "boolean" ? responseObject.hasMore : items.length >= numberOfResults,
      };
    },
    [currentWorkspaceId]
  );

  // Cache all recordings for search suggestions
  useEffect(() => {
    let cancelled = false;
    const loadAll = async () => {
      try {
        const ownerUserUIDFilter =
          currentWorkspaceId && workspaceFilter === WORKSPACE_FILTER_OWNED ? currentUserId : null;
        const response = await fetchRecordingsForContext({
          pageOffset: 0,
          numberOfResults: 100,
          searchKeyword: "",
          assetType: null,
          ownerUserUID: ownerUserUIDFilter,
        });
        if (!cancelled) {
          setAllCachedRecordings(response.items);
        }
      } catch {
        if (!cancelled) {
          setAllCachedRecordings([]);
        }
      }
    };
    loadAll();
    return () => {
      cancelled = true;
    };
  }, [currentUserId, currentWorkspaceId, fetchRecordingsForContext, refreshTick, workspaceFilter]);

  const recordingLookup = useMemo(() => {
    const result = new Map<string, RecordingCardData>();
    allCachedRecordings.forEach((recording) => result.set(recording.id, recording));
    searchResults.forEach((recording) => result.set(recording.id, recording));
    return result;
  }, [allCachedRecordings, searchResults]);

  const openRecording = useCallback((recording: RecordingCardData | undefined) => {
    if (!recording) return;
    const url = recording.url || recording.audioUrl || recording.videoUrl || recording.previewUrl;
    if (!url) return;
    window.open(url, "_blank", "noopener,noreferrer");
  }, []);

  const downloadRecording = useCallback((recording: RecordingCardData | undefined) => {
    if (!recording) return;
    const url = recording.url || recording.audioUrl || recording.videoUrl;
    if (!url) return;
    const link = document.createElement("a");
    link.href = url;
    link.download = recording.name || "recording";
    link.target = "_blank";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  }, []);

  const handleDeleteRecording = useCallback(
    async (recording: RecordingCardData | undefined) => {
      if (!recording) return;
      const confirmed = await (Utils as unknown as { getInstance: () => { showUserConfirmationDialog: (title: string, message: string) => Promise<boolean> } })
        .getInstance()
        .showUserConfirmationDialog(t("DELETE_RECORDING"), t("CONFIRM_DELETE_RECORDING").replace("{name}", recording.name));
      if (!confirmed) return;
      try {
        await TrebbleClientAPI.getInstance().deleteRecording({
          workspaceId: currentWorkspaceId || undefined,
          audioUID: recording.id,
        });
        setRefreshTick((tick) => tick + 1);
        (window as unknown as { alertSuccessMessage?: (message: string) => void }).alertSuccessMessage?.(t("RECORDING_DELETED"));
      } catch (error) {
        console.error("Failed to delete recording:", error);
        (window as unknown as { alertErrorMessage?: (message: string) => void }).alertErrorMessage?.(t("UNABLE_TO_DELETE_RECORDING"));
      }
    },
    [currentWorkspaceId]
  );

  const handleRecordingOpen = useCallback(
    (recordingId: string) => openRecording(recordingLookup.get(recordingId)),
    [openRecording, recordingLookup]
  );

  const handleRecordingDownload = useCallback(
    (recordingId: string) => downloadRecording(recordingLookup.get(recordingId)),
    [downloadRecording, recordingLookup]
  );

  const handleRecordingEditOrTranscribe = useCallback((recording: RecordingCardData | undefined) => {
    if (!recording) return;
    const router = PersistentModels.getInstance().getRouter();
    const recordingUrl = recording.url || recording.audioUrl || recording.videoUrl || recording.previewUrl;
    if (hasOpenableTranscription(recording) && recording.transcriptionId) {
      router.showCreateShortcastPageFromTranscription(recording.transcriptionId, false, true);
      return;
    }
    if (!recordingUrl) {
      (window as unknown as { alertErrorMessage?: (message: string) => void }).alertErrorMessage?.(t("UNABLE_TO_OPEN_RECORDING"));
      return;
    }
    router.showTranscribeAndEditAudioRecording(
      recording.id,
      recordingUrl,
      recording.durationInSeconds || null,
      hasOpenableTranscription(recording) ? recording.transcriptionId : null,
    );
  }, []);

  const handleRecordingMenuAction = useCallback(
    (recordingId: string, action: string) => {
      if (action === "edit" || action === "transcribe") {
        handleRecordingEditOrTranscribe(recordingLookup.get(recordingId));
        return;
      }
      if (action === "delete") {
        handleDeleteRecording(recordingLookup.get(recordingId));
      }
    },
    [handleDeleteRecording, handleRecordingEditOrTranscribe, recordingLookup]
  );

  const fetchLibraryData = useCallback(
    async (pageOffset: number, pageSize: number) => {
      const assetType = activeFilter === "all" ? null : activeFilter;
      const ownerUserUIDFilter =
        currentWorkspaceId && workspaceFilter === WORKSPACE_FILTER_OWNED ? currentUserId : null;

      const response = await fetchRecordingsForContext({
        pageOffset,
        numberOfResults: pageSize,
        searchKeyword: appliedSearch,
        assetType,
        sortBy: activeSort,
        ownerUserUID: ownerUserUIDFilter,
      });
      return { items: response.items, hasMore: response.hasMore };
    },
    [activeFilter, activeSort, appliedSearch, currentUserId, currentWorkspaceId, fetchRecordingsForContext, workspaceFilter]
  );

  const searchSuggestions = useMemo(() => {
    const normalizedQuery = searchQuery.trim().toLowerCase();
    if (!normalizedQuery) return [];

    const results: Array<{
      id: string;
      name: string;
      type: RecordingMediaType;
    }> = [];

    let count = 0;
    allCachedRecordings.forEach((recording) => {
      if (count >= 5 || !recordingMatchesSearch(recording, normalizedQuery)) return;
      results.push({ id: recording.id, name: recording.name, type: recording.type });
      count += 1;
    });
    return results;
  }, [allCachedRecordings, searchQuery]);

  const handleSuggestionClick = useCallback(
    (suggestion: { id: string }) => {
      handleRecordingOpen(suggestion.id);
    },
    [handleRecordingOpen]
  );

  const handleSearchChange = useCallback((query: string) => {
    setSearchQuery(query);
    if (!query.trim()) {
      setAppliedSearch("");
      setIsSearchResultsView(false);
      setSearchResults([]);
    }
  }, []);

  const handleSearchSubmit = useCallback(async () => {
    const normalizedQuery = searchQuery.trim();
    if (!normalizedQuery) return;
    setAppliedSearch(normalizedQuery);
    setSearchLoading(true);
    setIsSearchResultsView(true);
    try {
      const ownerUserUIDFilter =
        currentWorkspaceId && workspaceFilter === WORKSPACE_FILTER_OWNED ? currentUserId : null;
      const results: RecordingCardData[] = [];
      let pageOffset = 0;
      let hasMore = true;
      while (hasMore) {
        const response = await fetchRecordingsForContext({
          pageOffset,
          numberOfResults: RECORDING_SEARCH_PAGE_SIZE,
          searchKeyword: "",
          assetType: activeFilter === "all" ? null : activeFilter,
          sortBy: activeSort,
          ownerUserUID: ownerUserUIDFilter,
        });
        results.push(...response.items.filter((recording) => recordingMatchesSearch(recording, normalizedQuery)));
        hasMore = response.hasMore;
        pageOffset += 1;
      }
      setSearchResults(results);
    } catch (error) {
      console.error("Recording search failed:", error);
      setSearchResults([]);
    } finally {
      setSearchLoading(false);
    }
  }, [activeFilter, activeSort, currentUserId, currentWorkspaceId, fetchRecordingsForContext, searchQuery, workspaceFilter]);

  const handleCloseSearch = useCallback(() => {
    setSearchQuery("");
    setAppliedSearch("");
    setSearchResults([]);
    setIsSearchResultsView(false);
  }, []);

  const filterOptions = useMemo(
    () => [
      { key: "all", label: t("ALL") },
      { key: "audio", label: t("AUDIO") },
      { key: "video", label: t("VIDEO") },
    ],
    []
  );

  return (
    <div style={{ width: "100%", color: "var(--text-default-color)" }}>
      <div style={{ maxWidth: 1360, boxSizing: "border-box", margin: "0 auto", padding: "0 24px 32px" }}>
        <div style={{ padding: "12px 0 0" }}>
          <LibrarySearchBar
            searchQuery={searchQuery}
            onSearchChange={handleSearchChange}
            onSearchSubmit={handleSearchSubmit}
            onFilterClick={() => setFilterOpen(!filterOpen)}
            placeholder={t("SEARCH")}
            ti18n={ti18n}
            suggestions={searchSuggestions}
            showSuggestions={searchSuggestions.length > 0}
            onSuggestionClick={handleSuggestionClick}
            itemSectionLabel={t("RECORDINGS")}
            filterElement={
              <LibraryFilterPopup
                open={filterOpen}
                onOpenChange={setFilterOpen}
                activeFilter={activeFilter}
                onFilterChange={(value: string) => setActiveFilter(value as "all" | RecordingMediaType)}
                activeSort={activeSort}
                onSortChange={setActiveSort}
                ti18n={ti18n}
                typeOptions={filterOptions}
                sortOptions={SORT_OPTIONS.map((option) => ({ key: option.key, label: t(option.labelKey) }))}
              >
                <button
                  type="button"
                  onClick={() => setFilterOpen(!filterOpen)}
                  aria-label="Open filters"
                  style={{
                    background: "none",
                    border: "none",
                    padding: 4,
                    margin: 0,
                    cursor: "pointer",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    overflow: "visible",
                  }}
                >
                  <span style={{ position: "relative", display: "inline-flex", overflow: "visible" }}>
                    <i
                      className="pe-7s-filter"
                      style={{
                        WebkitTextStroke: "0.5px",
                        fontSize: "var(--text-lg)",
                        color:
                          filterOpen || activeFilter !== "all" || activeSort !== "newest"
                            ? "var(--trebble-primary, #EC7F54)"
                            : "var(--text-muted)",
                        lineHeight: 1,
                      }}
                    />
                    {(activeFilter !== "all" || activeSort !== "newest") && (
                      <span style={{ position: "absolute", top: 0, right: 0, width: 8, height: 8, backgroundColor: "var(--trebble-primary, #EC7F54)", transform: "translate(50%, -50%)" }} />
                    )}
                  </span>
                </button>
              </LibraryFilterPopup>
            }
          />
        </div>

        {isSearchResultsView ? (
          <LibrarySearchResults
            query={appliedSearch || searchQuery}
            results={searchResults.map((recording) => ({
              id: recording.id,
              name: recording.name,
              type: recording.type,
              date: recording.date,
              duration: recording.duration,
              folderName: t("RECORDINGS"),
            }))}
            onClose={handleCloseSearch}
            onResultClick={handleRecordingOpen}
            loading={searchLoading}
            ti18n={ti18n}
          />
        ) : (
          <>
            <LibraryToolbar
              activeFolder={null}
              folderPath={[]}
              onNavigateRoot={() => undefined}
              viewMode={viewMode}
              onViewModeChange={setViewMode}
              ti18n={ti18n}
              rootLabel={t("RECORDINGS")}
              showBreadcrumb={false}
            />

            {currentWorkspaceId && (
              <div style={{ margin: "0 0 24px", display: "flex", flexWrap: "wrap", gap: 8 }}>
                <TrebbleFilterButtons
                  filters={[
                    { key: WORKSPACE_FILTER_ALL, label: t("ALL_MEDIA_TAB") },
                    { key: WORKSPACE_FILTER_OWNED, label: t("ADDED_BY_ME_TAB") },
                  ]}
                  activeFilter={workspaceFilter}
                  onFilterChange={setWorkspaceFilter}
                  useEditorTheme={false}
                />
              </div>
            )}

            <div>
              <h3 className="library-section-label">
                {!currentWorkspaceId
                  ? t("RECORDINGS")
                  : workspaceFilter === WORKSPACE_FILTER_OWNED
                    ? t("ADDED_BY_ME_TAB")
                    : t("ALL_MEDIA_TAB")}
              </h3>
              <TrebbleInfiniteList
                key={`recordings-${currentWorkspaceId || "personal"}-${workspaceFilter}-${refreshTick}-${viewMode}-${appliedSearch}-${activeFilter}-${activeSort}`}
                containerId="recordingsInfiniteScroll"
                containerClassname={viewMode === "grid" ? "library-infinite-grid" : "library-infinite-list"}
                containerHeightMargin={320}
                fillAvailableViewport
                containerBottomMargin={24}
                pageSize={PAGE_SIZE}
                fetchData={fetchLibraryData}
                loader={<TrebbleSkeleton useEditorTheme={false} active paragraph={{ rows: 3 }} />}
                locale={{
                  emptyText: (
                    <EmptyState
                      message={
                        currentWorkspaceId && workspaceFilter === WORKSPACE_FILTER_OWNED
                          ? t("NO_ADDED_BY_ME_RECORDINGS_FOUND")
                          : t("NO_RECORDINGS_FOUND")
                      }
                      actionLabel={t("RECORD")}
                      onAction={() =>
                        PersistentModels.getInstance().getRouter().showCreateACapsulePageWithoutContext(null)
                      }
                      actionIconClassName="pe-7s-micro"
                    />
                  ),
                }}
                renderItem={(item: RecordingCardData) => {
                  const menuActions: ProjectCardMenuAction[] = [
                    hasOpenableTranscription(item) ? "edit" : "transcribe",
                  ];
                  if (item.ownerUserUID && item.ownerUserUID === currentUserId) {
                    menuActions.push("delete");
                  }

                  return (
                    <ProjectCard
                      key={item.id}
                      id={item.id}
                      name={item.name}
                      type={item.type}
                      audioUrl={item.audioUrl}
                      videoUrl={item.videoUrl}
                      previewUrl={item.previewUrl}
                      duration={item.duration}
                      date={item.date}
                      creator={item.creator}
                      viewMode={viewMode}
                      ti18n={ti18n}
                      primaryActionLabel={t("OPEN")}
                      primaryActionIconClassName="pe-7s-right-arrow"
                      menuActions={menuActions}
                      onPlay={handleRecordingOpen}
                      onDownload={handleRecordingDownload}
                      onClick={handleRecordingOpen}
                      onMenuAction={handleRecordingMenuAction}
                    />
                  );
                }}
              />
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default RecordingsLibraryPage;
